/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, computed, OnInit, signal} from '@angular/core';
import {EsciComponent} from "../esci.component";
import {ReleaseSchedule} from "../../model/releaseSchedule.model";

@Component({
    selector: 'app-release-schedule',
    templateUrl: './release-schedule.component.html',
    styleUrls: ['./release-schedule.component.scss'],
    standalone: false
})
export class ReleaseScheduleComponent extends EsciComponent implements OnInit {
    releaseDate = '';
    releaseTime: string = '16:00';
    displayTimezone = 'EST';
    noticeDays = 1;
    permissionUrl = '/esciUI/esciITAdminPermission';
    private readonly loadingSignal = signal(true);
    private readonly schedulesSignal = signal<ReleaseSchedule[]>([]);
    readonly activeReleaseForDisplay = computed<ReleaseSchedule | undefined>(() => {
        const startedRelease = this.schedulesSignal().find((schedule) => this.canShowFinish(schedule));
        if (startedRelease) {
            return startedRelease;
        }
        return this.schedulesSignal().find((schedule) => this.canShowStart(schedule));
    });
    readonly showCreateReleaseSection = computed(() => !this.activeReleaseForDisplay());

    ngOnInit(): void {
        this.permission.set(false);
        this.esciService.checkPermission(this);
        this.loadSchedules();
    }

    get loading(): boolean {
        return this.loadingSignal();
    }

    set loading(value: boolean) {
        this.loadingSignal.set(value);
    }

    get schedules(): ReleaseSchedule[] {
        return this.schedulesSignal();
    }

    set schedules(value: ReleaseSchedule[]) {
        this.schedulesSignal.set(value || []);
    }

    createSchedule(): void {
        if (!this.releaseDate || !this.releaseTime || !this.displayTimezone || this.noticeDays < 0) {
            alert('Please provide date, time, timezone, and notice days.');
            return;
        }
        const schedule: ReleaseSchedule = {
            releaseDateStr: this.toDisplayDate(this.releaseDate),
            releaseTimeStr: this.toDisplayTime(this.releaseTime),
            displayTimezone: this.displayTimezone.trim().toUpperCase(),
            noticeDays: this.noticeDays
        };
        this.loading = true;
        this.esciService.createReleaseSchedule(schedule).subscribe({
            next: () => {
                this.loading = false;
                this.loadSchedules();
            },
            error: (err) => {
                this.loading = false;
                alert('Failed to create release schedule. ' + (err?.error?.message ?? ''));
            }
        });
    }

    startRelease(schedule: ReleaseSchedule): void {
        if (!schedule.id) {
            return;
        }
        this.loading = true;
        this.esciService.startReleaseSchedule(schedule.id).subscribe({
            next: () => {
                this.loading = false;
                this.loadSchedules();
            },
            error: (err) => {
                this.loading = false;
                alert('Unable to start release. ' + (err?.error?.message ?? ''));
            }
        });
    }

    finishRelease(schedule: ReleaseSchedule): void {
        if (!schedule.id) {
            return;
        }
        this.loading = true;
        this.esciService.finishReleaseSchedule(schedule.id).subscribe({
            next: () => {
                this.loading = false;
                this.loadSchedules();
            },
            error: (err) => {
                this.loading = false;
                alert('Unable to finish release. ' + (err?.error?.message ?? ''));
            }
        });
    }

    cancelRelease(schedule: ReleaseSchedule): void {
        if (!schedule.id) {
            return;
        }
        this.loading = true;
        this.esciService.cancelReleaseSchedule(schedule.id).subscribe({
            next: () => {
                this.loading = false;
                this.loadSchedules();
            },
            error: (err) => {
                this.loading = false;
                alert('Unable to cancel release. ' + (err?.error?.message ?? ''));
            }
        });
    }

    canShowStart(schedule: ReleaseSchedule): boolean {
        return schedule.status === 'SCHEDULED' && !!schedule.canStartToday;
    }

    canShowFinish(schedule: ReleaseSchedule): boolean {
        return schedule.status === 'STARTED';
    }

    canShowCancel(schedule: ReleaseSchedule): boolean {
        if (schedule.status !== 'SCHEDULED') {
            return false;
        }
        return !this.schedulesSignal().some((row) => row.status === 'STARTED');
    }


    formatDisplayTime(timeValue?: string): string {
        if (!timeValue) {
            return '';
        }
        // Normalize legacy 24h values from db to 12h display.
        if (/^\d{1,2}:\d{2}$/.test(timeValue.trim())) {
            return this.toDisplayTime(timeValue.trim());
        }
        return timeValue;
    }

    getStatusSeverity(status?: string): 'success' | 'warn' | 'info' | 'secondary' {
        switch ((status || '').toUpperCase()) {
            case 'STARTED':
                return 'warn';
            case 'COMPLETED':
                return 'success';
            case 'SCHEDULED':
                return 'info';
            default:
                return 'secondary';
        }
    }

    private loadSchedules(): void {
        this.loading = true;
        this.esciService.getReleaseSchedules().subscribe({
            next: (schedules) => {
                this.schedules = schedules || [];
                // order schedules by id desc
                this.schedules.sort((a, b) => (b.id || 0) - (a.id || 0));
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.schedules = [];
            }
        });
    }

    private toDisplayDate(dateValue: string): string {
        // HTML date input gives yyyy-mm-dd; convert to M/d/yyyy for display strings.
        const parts = dateValue.split('-');
        if (parts.length !== 3) {
            return dateValue;
        }
        const month = Number(parts[1]);
        const day = Number(parts[2]);
        const year = Number(parts[0]);
        return `${month}/${day}/${year}`;
    }

    private toDisplayTime(timeValue: string): string {
        const parts = timeValue.split(':');
        if (parts.length < 2) {
            return timeValue;
        }
        const hour24 = Number(parts[0]);
        const minute = Number(parts[1]);
        if (Number.isNaN(hour24) || Number.isNaN(minute)) {
            return timeValue;
        }
        const meridiem = hour24 >= 12 ? 'pm' : 'am';
        const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
        return `${hour12}:${minute.toString().padStart(2, '0')} ${meridiem}`;
    }
}
